import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import type { User, SleepLog } from '../types';
import { 
    signInWithPassword, 
    signUpWithPassword, 
    resendVerification as apiResendVerification, 
    signOut 
} from '../services/supabase';

interface UserContextType {
  user: User;
  isLoading: boolean;
  updateUser: (updates: Partial<User>) => void;
  toggleFavourite: (trackId: string) => void;
  addSleepLog: (log: SleepLog) => void;
  deleteCustomTrack: (trackId: string) => void;
  resetApp: () => void;
  upgradeToPremium: () => void;
  // Auth methods
  loginWithPassword: (email: string, pass: string) => Promise<any>;
  signupWithPassword: (email: string, pass: string) => Promise<any>;
  resendVerification: (email: string) => Promise<any>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);
const USER_STORAGE_KEY = 'slumber-archive-profile';

const DEFAULT_USER: User = {
    name: 'Dreamer',
    onboardingStep: 5, // Default to completed step
    goals: [],
    sounds: [],
    currentSleepHours: null,
    currentSleepQuality: null,
    stressSources: [],
    ageRange: null,
    bedtime: null,
    hasCompletedOnboarding: true, // Bypass onboarding
    favourites: [],
    history: [],
    sleepLog: [],
    bedtimeReminderOn: false,
    notificationsOn: false,
    subscription: 'free'
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(DEFAULT_USER);
  const [isLoading, setIsLoading] = useState(true);

  // Load from LocalStorage on mount
  useEffect(() => {
    const init = () => {
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);
      if (storedUser) {
        try {
            const parsed = JSON.parse(storedUser);
            setUser(prev => ({ ...prev, ...parsed }));
        } catch(e) { console.error("Error parsing local storage", e); }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => {
        const updatedUser = { ...prev, ...updates };
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
        return updatedUser;
    });
  };

  const toggleFavourite = (trackId: string) => {
    const isFav = user.favourites.includes(trackId);
    let newFavs = [];
    if (isFav) {
        newFavs = user.favourites.filter(id => id !== trackId);
    } else {
        newFavs = [...user.favourites, trackId];
    }
    updateUser({ favourites: newFavs });
  };

  const addSleepLog = (log: SleepLog) => {
    const newLogs = [...user.sleepLog, log];
    updateUser({ sleepLog: newLogs });
  };

  const deleteCustomTrack = (_trackId: string) => {
      // Placeholder
  };
  
  const resetApp = () => {
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(DEFAULT_USER);
      window.location.reload();
  };

  const upgradeToPremium = () => {
    updateUser({ subscription: 'premium' });
  };

  const loginWithPasswordFn = async (email: string, pass: string) => {
      const result = await signInWithPassword(email, pass);
      // In a real application, you would sync the user profile from the database here
      return result;
  };

  const signupWithPasswordFn = async (email: string, pass: string) => {
      return await signUpWithPassword(email, pass);
  };

  const resendVerificationFn = async (email: string) => {
      return await apiResendVerification(email);
  };

  const logout = async () => {
      await signOut();
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(DEFAULT_USER);
  };

  return (
    <UserContext.Provider value={{ 
        user, isLoading, updateUser, toggleFavourite, 
        addSleepLog, deleteCustomTrack, resetApp,
        upgradeToPremium,
        loginWithPassword: loginWithPasswordFn,
        signupWithPassword: signupWithPasswordFn,
        resendVerification: resendVerificationFn,
        logout
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};